<template>
	<div>
		<V3Table
			:columns="customersColumns"
			:rows="customersRows"
			:onCellClick="onCellClick"
		>
			<div slot="header">
				<div>Customers with Special Prices</div>
			</div>
			<div slot="footer">
				<div style="padding-top: 10px">
					<span style="margin-left: 15px">
						Total customers with special prices: {{ customersRows.length }}
					</span>
				</div>
			</div>
		</V3Table>

		<!-- Customer Special Prices Modal -->
		<CustomerSpecialPricesModal
			:visible="modalVisible"
			:customer="selectedCustomer"
			@close="closeModal"
			@update-price="handleUpdatePrice"
			@delete-price="handleDeletePrice"
			@open-add-modal="openAddPriceModal"
		/>

		<!-- Add Special Price Modal -->
		<AddSpecialPriceModal
			v-if="selectedCustomer"
			:visible="addModalVisible"
			:customer="selectedCustomer"
			:existingPrices="selectedCustomer.customer_price_lists || []"
			@close="closeAddModal"
			@save-price="handleSaveNewPrice"
		/>
	</div>
</template>

<script>
import V3Table from '@/_srcv2/components/v3/v3-table/V3Table.vue'
import CustomerSpecialPricesModal from './CustomerSpecialPricesModal.vue'
import AddSpecialPriceModal from './AddSpecialPriceModal.vue'
import useCustomerSpecialPrices from '../composables/useCustomerSpecialPrices'
import { useGetCustomersSpecialPrices } from '../services/getCustomersSpecialPricesService'
import { useUpdateCustomerSpecialPrice } from '../services/updateCustomerSpecialPriceService'
import { useDeleteCustomerSpecialPrice } from '../services/deleteCustomerSpecialPriceService'
import { useAddCustomerSpecialPrice } from '../services/addCustomerSpecialPriceService'
import { onMounted, watch, ref } from '@vue/composition-api'

export default {
	name: 'CustomersTableForSpecialPrices',
	components: { V3Table, CustomerSpecialPricesModal, AddSpecialPriceModal },
	props: {
		companyId: {
			type: String,
			required: true,
		},
	},
	setup(props, { emit }) {
		// State for AddSpecialPriceModal
		const addModalVisible = ref(false)

		const {
			customersColumns,
			customersRows,
			loading,
			error,
			modalVisible,
			selectedCustomer,
			setCustomersData,
			setLoading,
			setError,
			getCustomerById,
			openModal,
			closeModal,
			updateLocalPrice,
			deleteLocalPrice,
			addLocalPrice,
			setIsUpdating,
			setIsDeleting,
			setMutationError,
		} = useCustomerSpecialPrices()

		const {
			customers,
			loading: queryLoading,
			error: queryError,
			refetch: refetchCustomers,
		} = useGetCustomersSpecialPrices(props.companyId)

		const { executeUpdate } = useUpdateCustomerSpecialPrice()
		const { executeDelete } = useDeleteCustomerSpecialPrice()
		const { executeAdd } = useAddCustomerSpecialPrice()

		watch(queryLoading, (newLoading) => {
			setLoading(newLoading)
		})

		watch(queryError, (newError) => {
			if (newError) {
				setError(newError)
				console.error('Error fetching customers:', newError)
			}
		})

		watch(customers, (newCustomers) => {
			if (newCustomers) {
				setCustomersData(newCustomers)
			}
		})

		const onCellClick = (params) => {
			if (params.column.field === 'view') {
				const customer = getCustomerById(params.row.customer_id)
				if (customer) {
					openModal(customer)
					emit('view-customer', customer)
				}
			}
		}

		const handleUpdatePrice = async (priceData) => {
			try {
				setIsUpdating(true)
				setMutationError(null)

				const result = await executeUpdate({
					company_id: props.companyId,
					customer_id: priceData.customer_id,
					stock_id: priceData.stock_id,
					customers_price: priceData.customers_price,
				})

				if (result) {
					// Update local state
					updateLocalPrice(
						priceData.customer_id,
						priceData.stock_id,
						priceData.customers_price,
					)
					console.log('Price updated successfully')
				}
			} catch (error) {
				console.error('Failed to update price:', error)
				setMutationError(error)
			} finally {
				setIsUpdating(false)
			}
		}

		const handleDeletePrice = async (priceData) => {
			try {
				setIsDeleting(true)
				setMutationError(null)

				const result = await executeDelete({
					company_id: props.companyId,
					customer_id: priceData.customer_id,
					stock_id: priceData.stock_id,
				})

				if (result) {
					// Update local state
					deleteLocalPrice(priceData.customer_id, priceData.stock_id)
					console.log('Price deleted successfully')
				}
			} catch (error) {
				console.error('Failed to delete price:', error)
				setMutationError(error)
			} finally {
				setIsDeleting(false)
			}
		}

		// Handlers for AddSpecialPriceModal
		const openAddPriceModal = () => {
			addModalVisible.value = true
		}

		const closeAddModal = () => {
			addModalVisible.value = false
		}

		const handleSaveNewPrice = async (priceData) => {
			try {
				setIsUpdating(true)
				setMutationError(null)

				const result = await executeAdd({
					company_id: props.companyId,
					customer_id: priceData.customer_id,
					stock_id: priceData.stock_id,
					customers_price: priceData.customers_price,
				})

				if (result) {
					// Create the price data object in the expected format
					const newPriceData = {
						customers_price: priceData.customers_price,
						stock: {
							stock_id: priceData.stock_id,
							stock_name: priceData.stock_name || '',
						},
					}

					// Add to local state
					if (addLocalPrice) {
						addLocalPrice(priceData.customer_id, newPriceData)
					}
					// Close the add modal
					closeAddModal()
					console.log('New special price added successfully')
				}
			} catch (error) {
				console.error('Failed to add special price:', error)
				setMutationError(error)
			} finally {
				setIsUpdating(false)
			}
		}

		onMounted(() => {
			console.log('CustomersTableForSpecialPrices mounted')
		})

		// Expose refetch method for parent component
		const refreshData = async () => {
			try {
				await refetchCustomers()
			} catch (err) {
				console.error('Error refreshing customer data:', err)
			}
		}

		return {
			customersColumns,
			customersRows,
			loading,
			error,
			modalVisible,
			selectedCustomer,
			addModalVisible,
			onCellClick,
			closeModal,
			handleUpdatePrice,
			handleDeletePrice,
			openAddPriceModal,
			closeAddModal,
			handleSaveNewPrice,
			refreshData,
		}
	},
}
</script>

<style lang="scss" scoped></style>
